// src/pages/admin/products/Categories.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AddCategoryModal from '../../../components/modals/AddCategoryModal'
import ViewCategoryModal from '../../../components/modals/ViewCategoryModal'
import EditCategoryModal from '../../../components/modals/EditCategoryModal'
import DeleteCategoryModal from '../../../components/modals/DeleteCategoryModal'
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  MoreVertical,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Package,
  X,
  Layers,
  Tag
} from 'lucide-react'
import CategoryService from '../../../services/categoryService'
import PageLoader from '../../../components/ui/PageLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

export const Categories = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [actionMenuOpen, setActionMenuOpen] = useState(null)
  const [actionLoading, setActionLoading] = useState({})
  const [toast, setToast] = useState(null)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate category object
  const validateCategory = (category) => {
    if (!category || typeof category !== 'object') return false
    return category._id && category.name
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Calculate statistics
  const getStatistics = () => {
    const totalCategories = categories.length
    const activeCategories = categories.filter(cat => cat.isActive).length
    const inactiveCategories = totalCategories - activeCategories
    
    // Get unique types (assuming we have type field, otherwise use a default)
    const types = [...new Set(categories.map(cat => cat.type || 'General'))].length
    
    return {
      total: totalCategories,
      active: activeCategories,
      inactive: inactiveCategories,
      types: types || 1
    }
  }

  // Fetch categories using service
  const fetchCategories = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await CategoryService.getAllCategories()
      
      if (response && response.success) {
        const categoriesData = safeGet(response, 'data.categories', [])
        // Filter out invalid categories
        const validCategories = Array.isArray(categoriesData) 
          ? categoriesData.filter(validateCategory)
          : []
        
        console.log('📦 Valid categories loaded:', validCategories.length)
        setCategories(validCategories)
      } else {
        console.warn('⚠️ Unexpected response format:', response)
        setError('Failed to load categories')
        setCategories([])
      }
    } catch (error) {
      console.error('❌ Error fetching categories:', error)
      setError(error.message || 'Failed to load categories')
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    fetchCategories()
  }, [])

  // Filter categories based on search term
  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    const name = safeGet(category, 'name', '').toLowerCase()
    const description = safeGet(category, 'description', '').toLowerCase()
    
    return name.includes(searchLower) || description.includes(searchLower)
  })

  // Handle category added
  const handleCategoryAdded = (newCategory) => {
    if (validateCategory(newCategory)) {
      setCategories(prev => [newCategory, ...prev])
      showToast('Category created successfully')
    }
  }

  // Handle category updated
  const handleCategoryUpdated = (updatedCategory) => {
    if (validateCategory(updatedCategory)) {
      setCategories(prev => 
        prev.map(category => 
          category._id === updatedCategory._id ? updatedCategory : category
        )
      )
      showToast('Category updated successfully')
    }
  }

  // Handle category deleted
  const handleCategoryDeleted = (categoryId) => {
    setCategories(prev => prev.filter(category => category._id !== categoryId))
    showToast('Category deleted successfully')
  }

  // Handle status toggle
  const handleStatusToggle = async (category) => {
    if (!category?._id) return

    const newStatus = !category.isActive
    setActionLoading(prev => ({ ...prev, [category._id]: true }))

    try {
      const response = await CategoryService.toggleCategoryStatus(category._id, newStatus)
      
      if (response.success) {
        setCategories(prev => 
          prev.map(cat => 
            cat._id === category._id 
              ? { ...cat, isActive: newStatus }
              : cat
          )
        )
        showToast(`Category ${newStatus ? 'activated' : 'deactivated'} successfully`)
      } else {
        showToast('Failed to update category status', 'error')
      }
    } catch (error) {
      console.error('Error toggling category status:', error)
      showToast('Failed to update category status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [category._id]: false }))
    }
  }

  // Handle action menu toggle
  const handleActionMenuToggle = (categoryId) => {
    setActionMenuOpen(actionMenuOpen === categoryId ? null : categoryId)
  }

  // Handle view category
  const handleViewCategory = (category) => {
    setSelectedCategory(category)
    setShowViewModal(true)
    setActionMenuOpen(null)
  }

  // Handle edit category
  const handleEditCategory = (category) => {
    setSelectedCategory(category)
    setShowEditModal(true)
    setActionMenuOpen(null)
  }

  // Handle delete category
  const handleDeleteCategory = (category) => {
    setSelectedCategory(category)
    setShowDeleteModal(true)
    setActionMenuOpen(null)
  }

  const statistics = getStatistics()

  // Loading state
  if (loading && categories.length === 0) {
    return (
      <PageLoader 
        headerTitle="Categories"
        title="Loading categories..."
      />
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
            </div>
            <div className="flex space-x-3">
              <Button 
                onClick={fetchCategories}
                leftIcon={RefreshCw}
                variant="outline"
                className="bg-white border-gray-300"
              >
                Refresh
              </Button>
              <Button 
                onClick={() => setShowAddModal(true)}
                leftIcon={Plus}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Add Category
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading categories</h3>
                <p className="text-gray-500 mb-4">{error}</p>
                <Button onClick={fetchCategories} leftIcon={RefreshCw}>
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={fetchCategories}
              leftIcon={RefreshCw}
              variant="outline"
              disabled={loading}
              className="bg-white border-gray-300"
            >
              Refresh
            </Button>
            <Button 
              onClick={() => setShowAddModal(true)}
              leftIcon={Plus}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Add Category
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Categories */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Layers className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Categories</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Categories */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inactive Categories */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <XCircle className="h-8 w-8 text-gray-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Inactive</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.inactive}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              placeholder="Search categories by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value || '')}
              className="pl-10 bg-white border-gray-300 h-12 w-full"
            />
          </div>
        </div>

        {/* Categories Table */}
        <Card className="bg-white">
          <CardContent className="p-0">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">📦</div>
                <h3 className="text-lg font-medium mb-2">No categories found</h3>
                <p className="text-gray-500">
                  {searchTerm ? 'Try adjusting your search criteria' : 'Add your first category to get started'}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category Name
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="pl-14 pr-1 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCategories.map((category) => (
                          <tr key={category._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-2xl mr-3">
                                  📦
                                </span>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {category.name}
                                  </div>
                                  {/* {category.description && (
                                    <div className="text-sm text-gray-500 truncate max-w-xs">
                                      {category.description}
                                    </div>
                                  )} */}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-500 max-w-xs truncate block">
                                {category.description || 'No description provided'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleStatusToggle(category)}
                                disabled={actionLoading[category._id]}
                                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                  category.isActive 
                                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                } ${actionLoading[category._id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {actionLoading[category._id] && (
                                  <ButtonSpinner />
                                )}
                                {category.isActive ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-1">
                                <button
                                  onClick={() => handleViewCategory(category)}
                                  className="text-gray-400 hover:text-gray-600 p-1"
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEditCategory(category)}
                                  className="text-gray-400 hover:text-gray-600 p-1"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category)}
                                  className="text-gray-400 hover:text-red-600 p-1"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4 p-4">
                  {filteredCategories.map((category) => (
                    <div key={category._id} className="border rounded-lg p-4 bg-white">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <div className="p-2 bg-blue-100 rounded-lg mr-3">
                              <Package className="h-5 w-5 text-blue-600" />
                            </div>
                            <h3 className="font-medium text-gray-900">{category.name}</h3>
                          </div>
                          <p className="text-sm text-gray-500 mb-2">
                            {category.description || 'No description provided'}
                          </p>
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => handleActionMenuToggle(category._id)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>
                          
                          {actionMenuOpen === category._id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border">
                              <div className="py-1">
                                <button
                                  onClick={() => handleViewCategory(category)}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Category
                                </button>
                                <button
                                  onClick={() => handleEditCategory(category)}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Category
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category)}
                                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Category
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => handleStatusToggle(category)}
                          disabled={actionLoading[category._id]}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            category.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          } ${actionLoading[category._id] ? 'opacity-50' : 'hover:opacity-80'}`}
                        >
                          {actionLoading[category._id] && (
                            <ButtonSpinner />
                          )}
                          {category.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <AddCategoryModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onCategoryAdded={handleCategoryAdded}
        />

        <ViewCategoryModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          category={selectedCategory}
        />

        <EditCategoryModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onCategoryUpdated={handleCategoryUpdated}
          category={selectedCategory}
        />

        <DeleteCategoryModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onCategoryDeleted={handleCategoryDeleted}
          category={selectedCategory}
        />
      </div>
    </div>
  )
}